/**
 * When the agent replaces manuscript markdown, models often emit single newlines between
 * paragraphs even when the removed passage used blank lines (\n\n). Infer spacing from the
 * replaced segment and apply it to new_text only when appropriate.
 */

/** Newline count between consecutive non-empty lines (1 = tight, 2+ = blank line between paragraphs). */
export function getInterParagraphGapSizes(text: string): number[] {
  const lines = text.split(/\n/);
  const gaps: number[] = [];
  let i = 0;
  while (i < lines.length) {
    while (i < lines.length && lines[i].trim() === "") i += 1;
    if (i >= lines.length) break;
    let j = i + 1;
    let emptyLines = 0;
    while (j < lines.length && lines[j].trim() === "") {
      emptyLines += 1;
      j += 1;
    }
    if (j < lines.length) {
      gaps.push(emptyLines + 1);
    }
    i = j;
  }
  return gaps;
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? Math.round((sorted[mid - 1] + sorted[mid]) / 2) : sorted[mid];
}

/**
 * Manuscript "blank lines" are empty paragraphs (often serialized as an NBSP-only line), not mere `\n\n`
 * between adjacent TipTap paragraphs.
 */
export function replacedSegmentUsesVisualBlankLines(text: string): boolean {
  const raw = String(text ?? "");
  if (!raw) return false;
  return /(?:^|\n)\u00a0\s*(?:\n|$)/m.test(raw);
}

/**
 * Preferred newline run between prose paragraphs in this passage, or null if not inferable.
 */
export function inferParagraphSeparatorNewlineCount(replacedSegment: string): number | null {
  if (replacedSegmentUsesVisualBlankLines(replacedSegment)) {
    return 2;
  }
  const gaps = getInterParagraphGapSizes(replacedSegment);
  if (gaps.length === 0) return null;

  const spacedGaps = gaps.filter((g) => g >= 2);
  const tightGaps = gaps.filter((g) => g === 1);

  // Majority used blank lines — preserve that rhythm in the replacement.
  if (spacedGaps.length > tightGaps.length) {
    return Math.max(2, median(spacedGaps));
  }

  // Majority tight — do not expand model output (every gap is tight or spaced).
  return 1;
}

const CLOSING_LINE_END = /[…\u201d\u2019"']\s*$/;
const SENTENCE_END = /[.!?]\s*$/;
const OPENING_LINE_START = /^[(\u201c"'\u2018A-Z]/;

/** Conservative: avoid treating dialogue line breaks or mid-sentence wraps as paragraph ends. */
export function isLikelyParagraphBoundary(previousLine: string, nextLine: string): boolean {
  const prev = previousLine.trim();
  const next = nextLine.trim();
  if (!prev || !next) return false;

  // Continuation / dialogue flow
  if (/[,;:]\s*$/.test(prev)) return false;
  if (/^\s*[-–—]\s/.test(next)) return false;

  // Sentence/quote/ellipsis end: require next line to open like a new sentence or speech
  if (OPENING_LINE_START.test(next) && (SENTENCE_END.test(prev) || CLOSING_LINE_END.test(prev))) {
    return true;
  }

  return false;
}

function countLikelyParagraphBoundaries(text: string): number {
  const lines = text.split(/\n/);
  let count = 0;
  for (let i = 1; i < lines.length; i += 1) {
    if (lines[i].trim() === "" || lines[i - 1].trim() === "") continue;
    if (isLikelyParagraphBoundary(lines[i - 1], lines[i])) count += 1;
  }
  return count;
}

export function expandParagraphSpacing(text: string, newlineCount: number): string {
  const sep = "\n".repeat(Math.max(2, newlineCount));
  const lines = text.split(/\n/);
  if (lines.length <= 1) return text;

  let result = lines[0];
  for (let i = 1; i < lines.length; i += 1) {
    const prev = lines[i - 1];
    const curr = lines[i];
    if (curr.trim() === "" || prev.trim() === "") {
      result += `\n${curr}`;
      continue;
    }
    const gap = isLikelyParagraphBoundary(prev, curr) ? sep : "\n";
    result += gap + curr;
  }
  return result;
}

function replacementAlreadySpaced(newText: string, targetNewlineCount: number): boolean {
  if (targetNewlineCount < 2) return true;
  const gaps = getInterParagraphGapSizes(newText);
  if (gaps.length === 0) return false;
  const spaced = gaps.filter((g) => g >= targetNewlineCount).length;
  const likelyBoundaries = countLikelyParagraphBoundaries(newText);
  if (likelyBoundaries === 0) return spaced > 0;
  return spaced >= likelyBoundaries;
}

/**
 * Align `newText` paragraph spacing with the passage it replaces when the model omitted blank lines.
 */
/** Whether plain text should be inserted as multiple manuscript paragraphs (not collapsed inline). */
export function isMultiParagraphPlainText(text: string): boolean {
  const clean = String(text ?? "");
  if (clean.includes("\n\n")) return true;
  const lines = clean.split("\n");
  for (let i = 1; i < lines.length; i += 1) {
    if (lines[i].trim() === "" || lines[i - 1].trim() === "") continue;
    if (isLikelyParagraphBoundary(lines[i - 1], lines[i])) return true;
  }
  return false;
}

type DocForSpacingInference = {
  forEach?: (
    f: (node: { isTextblock?: boolean; textContent?: string }, offset?: number, index?: number) => void,
  ) => void;
  textBetween?: (from: number, to: number, blockSeparator?: string, leafText?: string) => string;
  nodesBetween?: (from: number, to: number, f: (node: { isTextblock?: boolean }) => void) => void;
};

/**
 * Infer paragraph spacing rhythm from top-level editor blocks (empty paragraphs between content = blank line).
 */
export function inferParagraphSeparatorNewlineCountFromDoc(doc: DocForSpacingInference): number | null {
  if (typeof doc?.forEach !== "function") return null;

  const gaps: number[] = [];
  let emptyBetween = 0;
  let seenContent = false;

  doc.forEach((node) => {
    if (!node?.isTextblock) return;
    const isEmpty = String(node.textContent ?? "").trim().length === 0;
    if (isEmpty) {
      if (seenContent) emptyBetween += 1;
      return;
    }
    if (seenContent) {
      gaps.push(emptyBetween + 1);
    }
    seenContent = true;
    emptyBetween = 0;
  });

  if (gaps.length === 0) return null;

  const spacedGaps = gaps.filter((g) => g >= 2);
  const tightGaps = gaps.filter((g) => g === 1);

  if (spacedGaps.length > tightGaps.length) {
    return Math.max(2, median(spacedGaps));
  }
  return 1;
}

export function preserveReplacementParagraphSpacing(newText: string, replacedSegment: string): string {
  if (!newText || !replacedSegment) return newText;
  if (!replacedSegmentUsesVisualBlankLines(replacedSegment)) return newText;

  const targetNewlines = inferParagraphSeparatorNewlineCount(replacedSegment);
  if (targetNewlines === null || targetNewlines < 2) {
    return newText;
  }

  if (replacementAlreadySpaced(newText, targetNewlines)) {
    return newText;
  }

  return expandParagraphSpacing(newText, targetNewlines);
}

const MANUSCRIPT_SPACER_LINE = "\u00a0";

function paragraphIsManuscriptSpacerLine(line: string): boolean {
  return line.replace(/\u00a0/g, "").trim().length === 0;
}

/**
 * Manuscript markdown uses NBSP-only paragraphs between content blocks so TipTap shows a blank line
 * (paragraph margin is zero). Standard `\n\n` between paragraphs does not round-trip that spacing.
 */
/**
 * Add visible blank lines between manuscript paragraphs: expand likely paragraph breaks,
 * then insert NBSP-only spacer paragraphs so TipTap round-trips the spacing.
 */
export function formatManuscriptAddBlankLinesBetweenParagraphs(markdown: string): string {
  const raw = String(markdown ?? "").replace(/\r\n/g, "\n");
  if (!raw.trim()) return raw;
  // Pasted lines serialize as Markdown hard breaks, not separate paragraphs.
  // Promote those explicit breaks before sentence-boundary spacing inference.
  const paragraphBreaks = raw.replace(/(\\+| {2,})\n/g, (match, marker: string) => {
    if (marker.startsWith("\\")) {
      if (marker.length % 2 === 0) return match; // Escaped literal backslashes.
      return `${marker.slice(0, -1)}\n\n`;
    }
    return "\n\n";
  });
  const expanded = expandParagraphSpacing(paragraphBreaks, 2);
  return ensureManuscriptMarkdownSpacerParagraphs(expanded);
}

export function ensureManuscriptMarkdownSpacerParagraphs(markdown: string): string {
  const raw = String(markdown ?? "").replace(/\r\n/g, "\n");
  if (!raw.trim()) return raw;

  const blocks = raw.split(/\n\n+/);
  const contentBlocks: string[] = [];
  for (const block of blocks) {
    if (!block.trim()) continue;
    if (paragraphIsManuscriptSpacerLine(block)) continue;
    contentBlocks.push(block);
  }

  if (contentBlocks.length <= 1) return raw;
  return contentBlocks.join(`\n\n${MANUSCRIPT_SPACER_LINE}\n\n`);
}

/** Selection-sized replacements only; whole-chapter/document rewrites must not uniformize every gap. */
export const MAX_PARAGRAPH_SPACING_REFERENCE_CHARS = 8_000;

export function shouldInferParagraphSpacingFromReplacedSegment(replacedSegment: string): boolean {
  const raw = String(replacedSegment ?? "");
  if (replacedSegmentUsesVisualBlankLines(raw)) {
    return true;
  }
  return raw.length <= MAX_PARAGRAPH_SPACING_REFERENCE_CHARS;
}

/**
 * Plain-text spacing preservation plus manuscript markdown spacer paragraphs for server-side writes.
 */
export function applyManuscriptParagraphSpacing(newText: string, replacedSegment: string): string {
  const spaced = preserveReplacementParagraphSpacing(newText, replacedSegment);
  if (!replacedSegmentUsesVisualBlankLines(replacedSegment)) {
    return spaced;
  }
  return ensureManuscriptMarkdownSpacerParagraphs(spaced);
}

/** TipTap/ProseMirror: count paragraph-level blocks in a doc range (each usually has visual blank line after). */
export function countTextblocksInRange(
  doc: { nodesBetween?: (from: number, to: number, f: (node: { isTextblock?: boolean }) => void) => void },
  from: number,
  to: number,
): number {
  if (typeof doc?.nodesBetween !== "function") return 0;
  let count = 0;
  doc.nodesBetween(from, to, (node) => {
    if (node?.isTextblock) count += 1;
  });
  return count;
}

export function countContentTextblocksInRange(
  doc: {
    nodesBetween?: (
      from: number,
      to: number,
      f: (node: { isTextblock?: boolean; textContent?: string }) => void,
    ) => void;
  },
  from: number,
  to: number,
): number {
  if (typeof doc?.nodesBetween !== "function") return 0;
  let count = 0;
  doc.nodesBetween(from, to, (node) => {
    if (node?.isTextblock && String(node.textContent ?? "").trim().length > 0) {
      count += 1;
    }
  });
  return count;
}

/** True when the selection includes empty spacer paragraphs between content textblocks. */
export function selectionHasSpacerParagraphsInRange(
  doc: {
    nodesBetween?: (
      from: number,
      to: number,
      f: (node: { isTextblock?: boolean; textContent?: string }) => void,
    ) => void;
  },
  from: number,
  to: number,
): boolean {
  if (typeof doc?.nodesBetween !== "function") return false;
  let seenContent = false;
  let emptyBetween = 0;
  let foundSpacer = false;
  doc.nodesBetween(from, to, (node) => {
    if (!node?.isTextblock) return;
    const isEmpty = String(node.textContent ?? "").trim().length === 0;
    if (isEmpty) {
      if (seenContent) emptyBetween += 1;
      return;
    }
    if (seenContent && emptyBetween > 0) {
      foundSpacer = true;
    }
    seenContent = true;
    emptyBetween = 0;
  });
  return foundSpacer;
}

/**
 * Text representing what spacing to preserve. Multi-block selections use a proxy with blank lines
 * because `textBetween` often joins blocks with a single `\n` even when the editor shows spaced paragraphs.
 */
export function replacedSegmentForSpacingInference(doc: DocForSpacingInference, from: number, to: number): string {
  const contentTextblockCount = countContentTextblocksInRange(doc, from, to);
  if (contentTextblockCount >= 2) {
    const hasSpacers = selectionHasSpacerParagraphsInRange(doc, from, to);
    const separator = hasSpacers ? "\n\n\u00a0\n\n" : "\n";
    return Array(contentTextblockCount).fill(".").join(separator);
  }

  if (typeof doc.textBetween === "function") {
    try {
      return doc.textBetween(from, to, "\n", "\0");
    } catch {
      return doc.textBetween(from, to);
    }
  }
  return "";
}
